import { useCallback, useMemo, useRef, useState } from 'react';

import type { CatalogLanguage } from '../model/types';

/**
 * A per-locale draft's field values — always plain strings (never `undefined`) for easy binding to
 * a `TextField`. Exported for callers that want a loose `Record<string, string>` reference; the
 * hook itself constrains its own `F` with the self-referential `Record<keyof F, string>` below
 * instead, because a plain caller-declared interface (e.g. `{name: string; description: string}`,
 * with no explicit index signature) does not structurally satisfy `Record<string, string>` as a
 * generic TYPE ARGUMENT even though its property values are all strings — `Record<keyof F, string>`
 * expresses the same "every field is a string" requirement without demanding one.
 */
export type CardLocaleFields = Record<string, string>;

export interface UseCardLanguageDraftResult<F extends Record<keyof F, string>> {
  /** Enabled (`active`) registry languages, default first — empty when the tenant has none configured. */
  enabledLanguages: CatalogLanguage[];
  /** The tenant's content-default locale code, or `''` when the registry is empty (single-language fallback). */
  defaultLocale: string;
  /** The locale currently being viewed/edited — falls back to `defaultLocale` until the user picks one. */
  selectedLocale: string;
  selectLocale: (locale: string) => void;
  /** Current fields for `selectedLocale` (never `undefined` — an unseeded locale reads as all-blank). */
  fields: F;
  /** Update one field of the SELECTED locale — marks that locale dirty. */
  setField: <K extends keyof F>(key: K, value: F[K]) => void;
  /**
   * Seed one field of a locale from server data — a no-op if that exact `(locale, field)` pair has
   * already been seeded once (including seeded-then-since-edited), so a slower-resolving query can
   * never clobber a faster one nor an in-progress edit. Never marks the locale dirty.
   */
  seedField: <K extends keyof F>(locale: string, key: K, value: F[K]) => void;
  /** Fields drafted for an arbitrary locale (falls back to all-blank when unseeded). */
  fieldsFor: (locale: string) => F;
  /** True when the locale's drafted fields have any non-blank value. */
  isFilled: (locale: string) => boolean;
  dirtyLocales: ReadonlySet<string>;
  isDirty: boolean;
  /** `{locale, ...fields}` for every dirtied (user-touched) locale — the save payload's `translations[]` source. */
  touchedEntries: () => Array<{ locale: string } & F>;
  /** Wipes every draft + dirty/seeded state and reselects the default — call when the edited entity changes. */
  resetAll: () => void;
}

/**
 * Per-locale draft state for a multilingual master-data card (product/category) — the
 * merchant-portal "card-language switcher" (epic-catalog-004, adr-tenant-005, plan-tenant-004
 * Wave 2). Generic over the translatable field shape `F` (e.g. `{name: string; description:
 * string}`) so `ProductEditPage` and `CategoryFormDialog` share one implementation despite their
 * upsert contracts differing (a category has no flat `description` reference field — see
 * `CategoryUpsertRequest`'s doc comment).
 *
 * **Empty-registry fallback**: when the tenant has no configured catalog languages,
 * `enabledLanguages` is `[]` and `defaultLocale`/`selectedLocale` resolve to the single sentinel
 * locale `''` — the caller renders one plain field bound to `fields`/`setField` with no switcher,
 * reproducing today's single-language behaviour with no special-casing in the render path.
 */
export function useCardLanguageDraft<F extends Record<keyof F, string>>(
  languages: CatalogLanguage[],
  emptyFields: F,
): UseCardLanguageDraftResult<F> {
  const enabledLanguages = useMemo(
    () =>
      languages
        .filter((l) => l.active)
        .sort((a, b) => {
          if (a.defaultLocale !== b.defaultLocale) return a.defaultLocale ? -1 : 1;
          return a.name.localeCompare(b.name);
        }),
    [languages],
  );
  // The default language can never be disabled (adr-tenant-005) — read it off the full registry,
  // not `enabledLanguages`, so a defensively-stale/disabled-default snapshot still resolves.
  const defaultLocale = useMemo(() => languages.find((l) => l.defaultLocale)?.locale ?? '', [languages]);

  const [drafts, setDrafts] = useState<Record<string, F>>({});
  const [dirtyLocales, setDirtyLocales] = useState<ReadonlySet<string>>(new Set());
  const [selectedLocaleState, setSelectedLocaleState] = useState('');
  // Field-granular "have we already seeded this?" markers — see `seedField`'s doc.
  const seededRef = useRef<Set<string>>(new Set());

  const selectedLocale = selectedLocaleState || defaultLocale;

  const selectLocale = useCallback((locale: string) => setSelectedLocaleState(locale), []);

  const fieldsFor = useCallback((locale: string): F => drafts[locale] ?? emptyFields, [drafts, emptyFields]);

  const setField = useCallback(
    <K extends keyof F>(key: K, value: F[K]) => {
      const locale = selectedLocale;
      setDrafts((prev) => ({ ...prev, [locale]: { ...(prev[locale] ?? emptyFields), [key]: value } }));
      setDirtyLocales((prev) => (prev.has(locale) ? prev : new Set(prev).add(locale)));
    },
    [selectedLocale, emptyFields],
  );

  const seedField = useCallback(
    <K extends keyof F>(locale: string, key: K, value: F[K]) => {
      const marker = `${locale}:${String(key)}`;
      if (seededRef.current.has(marker)) return;
      seededRef.current.add(marker);
      setDrafts((prev) => ({ ...prev, [locale]: { ...(prev[locale] ?? emptyFields), [key]: value } }));
    },
    [emptyFields],
  );

  const isFilled = useCallback(
    (locale: string) =>
      // `F`'s own values ARE always strings (`Record<keyof F, string>`), but `Object.values` can't
      // see that through a non-index-signature generic — a `string[]` cast is exact, not an escape.
      (Object.values(fieldsFor(locale)) as string[]).some((v) => v.trim() !== ''),
    [fieldsFor],
  );

  const touchedEntries = useCallback(
    () => Array.from(dirtyLocales).map((locale) => ({ locale, ...fieldsFor(locale) })),
    [dirtyLocales, fieldsFor],
  );

  const resetAll = useCallback(() => {
    seededRef.current = new Set();
    setDrafts({});
    setDirtyLocales(new Set());
    setSelectedLocaleState('');
  }, []);

  return {
    enabledLanguages,
    defaultLocale,
    selectedLocale,
    selectLocale,
    fields: fieldsFor(selectedLocale),
    setField,
    seedField,
    fieldsFor,
    isFilled,
    dirtyLocales,
    isDirty: dirtyLocales.size > 0,
    touchedEntries,
    resetAll,
  };
}
